/**
 * Notification Service
 * Handles all user-notification API calls
 * Endpoints:
 *   GET  /api/user-notification/unread           – unread messages
 *   GET  /api/user-notification/history          – full history (with filters)
 *   POST /api/user-notification/{id}/mark-read   – mark one message as read
 */
import { api as enhancedApi } from './enhancedApiService';

const BASE_PATH = '/api/user-notification';

class NotificationService {
  /**
   * Get all unread system messages for the current user.
   * @returns {Promise<UserNotificationHistoryModel[]>}
   */
  async getUnread() {
    return enhancedApi.get(`${BASE_PATH}/unread`);
  }

  /**
   * Get notification history (all messages) with optional filters.
   * @param {Object} [params]
   * @param {boolean}  [params.IsRead]             – filter by read status
   * @param {string}   [params.Type]               – Info | Warning | Promotion | Other
   * @param {string}   [params.DeliveredDateFrom]  – ISO date-time string
   * @param {string}   [params.DeliveredDateTo]    – ISO date-time string
   * @param {number}   [params.UserNotificationId] – filter by notification id
   * @returns {Promise<UserNotificationHistoryModel[]>}
   */
  async getHistory(params = {}) {
    // Filter out empty/null values before passing to the API service
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    return enhancedApi.get(`${BASE_PATH}/history`, cleanParams);
  }

  /**
   * Mark a single notification as read.
   * @param {number} id – history record ID
   * @returns {Promise<boolean>}
   */
  async markRead(id) {
    return enhancedApi.post(`${BASE_PATH}/${id}/mark-read`, {});
  }
}

export default new NotificationService();
